import { useState, useRef, useCallback, useEffect } from "react";
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Dimensions,
  Alert,
  ActivityIndicator,
  TextInput,
  Modal,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { LinearGradient } from "expo-linear-gradient";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import ViewShot from "react-native-view-shot";
import * as ImagePicker from "expo-image-picker";
import { readAsStringAsync } from "expo-file-system/legacy";
import { decode } from "base64-arraybuffer";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { STICKER_PACKS, StickerPack, Sticker as PackSticker } from "@/lib/sticker-registry";
import {
  GestureDetector,
  Gesture,
  GestureHandlerRootView,
} from "react-native-gesture-handler";

// Conditionally import Skia - only available in development builds
let Canvas: any = null;
let Path: any = null;
let Skia: any = null;
let useCanvasRef: any = null;
let isSkiaAvailable = false;

try {
  const SkiaModule = require("@shopify/react-native-skia");
  Canvas = SkiaModule.Canvas;
  Path = SkiaModule.Path;
  Skia = SkiaModule.Skia;
  useCanvasRef = SkiaModule.useCanvasRef;
  isSkiaAvailable = true;
} catch (e) {
  // Skia not available (running in Expo Go or before rebuild)
  console.log("Skia not available - drawing feature requires development build");
}

// Type for Skia path (used when Skia is available)
type SkPath = any;

const { width: SCREEN_WIDTH } = Dimensions.get("window");
const CANVAS_SIZE = SCREEN_WIDTH - 48;

/**
 * Base element with common properties
 */
interface BaseElement {
  id: string;
  x: number;
  y: number;
  scale: number;
  rotation: number; // Rotation in degrees
  zIndex: number;
}

/**
 * Sticker element on canvas (emoji)
 */
interface StickerElement extends BaseElement {
  type: 'sticker';
  emoji: string;
}

/**
 * Text element on canvas
 */
interface TextElement extends BaseElement {
  type: 'text';
  text: string;
  color: string;
}

/**
 * Image sticker element on canvas (like WhatsApp stickers)
 */
interface ImageStickerElement extends BaseElement {
  type: 'image';
  uri: string;
}

/**
 * Shape element on canvas
 */
interface ShapeElement extends BaseElement {
  type: 'shape';
  shape: 'circle' | 'square' | 'heart' | 'star' | 'triangle';
  color: string;
}

/**
 * Union type for all canvas elements
 */
type CanvasElement = StickerElement | TextElement | ImageStickerElement | ShapeElement;

/**
 * Avatar design structure saved to database
 * Contains all elements and background info to recreate the design
 */
export interface AvatarDesign {
  version: number;
  backgroundColor: string;
  backgroundGradient: string[] | null;
  backgroundImage: string | null;
  elements: CanvasElement[];
  nextZIndex: number;
}

/**
 * Available background colors
 */
const COLORS = [
  "#FFFFFF", "#F3F4F6", "#FEE2E2", "#FEF3C7", "#D1FAE5", "#DBEAFE",
  "#E0E7FF", "#EDE9FE", "#FCE7F3", "#ECFDF5", "#FEF9C3", "#FFEDD5",
  "#EF4444", "#F97316", "#EAB308", "#22C55E", "#3B82F6", "#8B5CF6",
  "#EC4899", "#14B8A6", "#6366F1", "#1F2937", "#7C3AED", "#059669",
];

/**
 * Gradient presets for backgrounds
 */
const GRADIENTS = [
  { id: 'sunset', name: 'Zonsondergang', colors: ['#F97316', '#EC4899', '#8B5CF6'] },
  { id: 'ocean', name: 'Oceaan', colors: ['#06B6D4', '#3B82F6', '#8B5CF6'] },
  { id: 'forest', name: 'Bos', colors: ['#22C55E', '#14B8A6', '#06B6D4'] },
  { id: 'fire', name: 'Vuur', colors: ['#EF4444', '#F97316', '#EAB308'] },
  { id: 'cotton', name: 'Suikerspin', colors: ['#EC4899', '#A855F7', '#6366F1'] },
  { id: 'night', name: 'Nacht', colors: ['#1F2937', '#4B5563', '#6366F1'] },
  { id: 'gold', name: 'Goud', colors: ['#EAB308', '#F97316', '#EF4444'] },
  { id: 'rainbow', name: 'Regenboog', colors: ['#EF4444', '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6'] },
];

/**
 * Sticker categories
 */
const STICKER_CATEGORIES = [
  {
    name: "Accessoires",
    items: ["🎩", "👑", "🎀", "🧢", "👒", "🎭", "👓", "🕶️", "🥽", "🎪", "💍", "⌚"],
  },
  {
    name: "Gezichten",
    items: ["🥸", "🤡", "👻", "💀", "👽", "🤖", "😈", "👹", "👺", "🎃", "😎", "🤠"],
  },
  {
    name: "Dieren",
    items: ["🦁", "🐻", "🦊", "🐺", "🐯", "🦄", "🐲", "🦅", "🦋", "🐙", "🦈", "🐘"],
  },
  {
    name: "Sport",
    items: ["⚽", "🏀", "🏈", "⚾", "🎾", "🏆", "🥇", "🎯", "🎳", "🏋️", "🚴", "⛷️"],
  },
  {
    name: "Fun",
    items: ["⭐", "💫", "✨", "🔥", "💥", "❤️", "💜", "💎", "🚀", "🎉", "🎊", "🎁"],
  },
  {
    name: "Eten",
    items: ["🍕", "🍔", "🍟", "🍩", "🎂", "🍭", "🍫", "🍿", "🥑", "🌶️", "🍪", "🧁"],
  },
  {
    name: "Natuur",
    items: ["🌸", "🌺", "🌻", "🌴", "🌈", "☀️", "🌙", "⚡", "❄️", "🌊", "🏔️", "🌋"],
  },
  {
    name: "Symbolen",
    items: ["💪", "✌️", "🤙", "👊", "🙌", "👏", "💯", "♾️", "⚔️", "🛡️", "🔱", "⚜️"],
  },
];

/**
 * Available shapes
 */
const SHAPES: { id: ShapeElement['shape']; icon: string; label: string }[] = [
  { id: 'circle', icon: '●', label: 'Cirkel' },
  { id: 'square', icon: '■', label: 'Vierkant' },
  { id: 'heart', icon: '♥', label: 'Hart' },
  { id: 'star', icon: '★', label: 'Ster' },
  { id: 'triangle', icon: '▲', label: 'Driehoek' },
];

/**
 * Brush sizes for drawing
 */
const BRUSH_SIZES = [
  { size: 4, label: 'XS' },
  { size: 8, label: 'S' },
  { size: 14, label: 'M' },
  { size: 22, label: 'L' },
  { size: 32, label: 'XL' },
];

/**
 * Drawing stroke interface
 */
interface DrawingStroke {
  path: SkPath;
  color: string;
  strokeWidth: number;
}

/**
 * Drawing Canvas Modal Component
 */
interface DrawingModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (imageUri: string) => void;
  canvasSize: number;
}

/**
 * Opacity presets for brush
 */
const OPACITY_PRESETS = [
  { value: 0.25, label: "25%" },
  { value: 0.5, label: "50%" },
  { value: 0.75, label: "75%" },
  { value: 1, label: "100%" },
];

/**
 * Convert hex color to rgba string
 */
const hexToRgba = (hex: string, opacity: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const DrawingModal = ({ visible, onClose, onSave, canvasSize }: DrawingModalProps) => {
  const canvasRef = useCanvasRef();
  const [strokes, setStrokes] = useState<DrawingStroke[]>([]);
  const [currentPath, setCurrentPath] = useState<SkPath | null>(null);
  const [brushColor, setBrushColor] = useState("#1F2937");
  const [brushSize, setBrushSize] = useState(8);
  const [brushOpacity, setBrushOpacity] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEraser, setIsEraser] = useState(false);

  // Reset when modal opens
  useEffect(() => {
    if (visible) {
      setStrokes([]);
      setCurrentPath(null);
      setIsEraser(false);
      setBrushOpacity(1);
    }
  }, [visible]);

  // Get current drawing color (white if eraser, rgba with opacity if brush)
  const currentColor = isEraser ? "#FFFFFF" : hexToRgba(brushColor, brushOpacity);
  const currentSize = isEraser ? brushSize * 2 : brushSize; // Eraser is bigger

  /**
   * Handle touch start - begin new path
   */
  const handleTouchStart = (x: number, y: number) => {
    const path = Skia.Path.Make();
    path.moveTo(x, y);
    setCurrentPath(path);
    setIsDrawing(true);
  };

  /**
   * Handle touch move - continue path
   */
  const handleTouchMove = (x: number, y: number) => {
    if (!currentPath || !isDrawing) return;
    currentPath.lineTo(x, y);
    // Force re-render by creating new reference
    setCurrentPath(currentPath.copy());
  };

  /**
   * Handle touch end - save stroke
   */
  const handleTouchEnd = () => {
    if (currentPath && isDrawing) {
      setStrokes(prev => [...prev, {
        path: currentPath,
        color: currentColor,
        strokeWidth: currentSize,
      }]);
    }
    setCurrentPath(null);
    setIsDrawing(false);
  };

  /**
   * Undo last stroke
   */
  const handleUndo = () => {
    setStrokes(prev => prev.slice(0, -1));
  };

  /**
   * Clear all strokes
   */
  const handleClear = () => {
    Alert.alert("Wis tekening", "Weet je zeker dat je alles wilt wissen?", [
      { text: "Annuleer", style: "cancel" },
      {
        text: "Wissen",
        style: "destructive",
        onPress: () => setStrokes([]),
      },
    ]);
  };

  /**
   * Save drawing as image
   */
  const handleSave = async () => {
    if (strokes.length === 0) {
      Alert.alert("Geen tekening", "Maak eerst een tekening!");
      return;
    }

    try {
      // Get the Skia image from canvas
      const image = canvasRef.current?.makeImageSnapshot();
      if (!image) {
        Alert.alert("Fout", "Kon tekening niet opslaan");
        return;
      }

      // Convert to base64
      const base64 = image.encodeToBase64();
      const dataUri = `data:image/png;base64,${base64}`;
      
      onSave(dataUri);
    } catch (error) {
      console.error("Error saving drawing:", error);
      Alert.alert("Fout", "Kon tekening niet opslaan");
    }
  };

  /**
   * Handle native touch events (works better on Android in Modal)
   */
  const handleNativeTouchStart = (event: any) => {
    const touch = event.nativeEvent;
    handleTouchStart(touch.locationX, touch.locationY);
  };

  const handleNativeTouchMove = (event: any) => {
    const touch = event.nativeEvent;
    handleTouchMove(touch.locationX, touch.locationY);
  };

  const handleNativeTouchEnd = () => {
    handleTouchEnd();
  };

  // Don't render if Skia is not available
  if (!isSkiaAvailable || !Canvas || !Path) {
    return (
      <Modal visible={visible} animationType="slide" presentationStyle="fullScreen">
        <SafeAreaView className="flex-1 bg-gray-900 items-center justify-center" edges={["top", "bottom"]}>
          <Ionicons name="brush-outline" size={64} color="#6B7280" />
          <Text className="text-white text-lg font-bold mt-4">Tekenen niet beschikbaar</Text>
          <Text className="text-gray-400 text-center mt-2 px-8">
            Deze functie vereist een development build.{"\n"}
            Herbouw de app om te kunnen tekenen.
          </Text>
          <Pressable
            onPress={onClose}
            className="mt-6 bg-gray-700 px-6 py-3 rounded-xl"
          >
            <Text className="text-white font-medium">Sluiten</Text>
          </Pressable>
        </SafeAreaView>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="fullScreen">
      <SafeAreaView className="flex-1 bg-gray-900" edges={["top", "bottom"]}>
        {/* Header */}
        <View className="flex-row items-center justify-between px-4 py-3 border-b border-gray-700">
          <Pressable onPress={onClose} className="p-2">
            <Ionicons name="close" size={28} color="#FFFFFF" />
          </Pressable>
          <Text className="text-lg font-bold text-white">Tekenen 🎨</Text>
          <View className="flex-row items-center">
            <Pressable
              onPress={handleUndo}
              disabled={strokes.length === 0}
              className="p-2 mr-2"
            >
              <Ionicons
                name="arrow-undo"
                size={24}
                color={strokes.length === 0 ? "#6B7280" : "#FFFFFF"}
              />
            </Pressable>
            <Pressable onPress={handleClear} className="p-2">
              <Ionicons name="trash-outline" size={24} color="#EF4444" />
            </Pressable>
          </View>
        </View>

        {/* Drawing Canvas */}
        <View className="flex-1 items-center justify-center px-4 py-6">
          <View
            style={{
              width: canvasSize,
              height: canvasSize,
              borderRadius: 24,
              overflow: "hidden",
              backgroundColor: "#FFFFFF",
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.3,
              shadowRadius: 8,
              elevation: 8,
            }}
            onTouchStart={handleNativeTouchStart}
            onTouchMove={handleNativeTouchMove}
            onTouchEnd={handleNativeTouchEnd}
          >
            <Canvas
              ref={canvasRef}
              style={{ width: canvasSize, height: canvasSize }}
            >
              {/* Render all completed strokes */}
              {strokes.map((stroke, index) => (
                <Path
                  key={index}
                  path={stroke.path}
                  color={stroke.color}
                  style="stroke"
                  strokeWidth={stroke.strokeWidth}
                  strokeCap="round"
                  strokeJoin="round"
                />
              ))}
              {/* Render current stroke being drawn */}
              {currentPath && (
                <Path
                  path={currentPath}
                  color={currentColor}
                  style="stroke"
                  strokeWidth={currentSize}
                  strokeCap="round"
                  strokeJoin="round"
                />
              )}
            </Canvas>
          </View>

          <Text className="text-gray-500 text-xs mt-3 text-center">
            Teken met je vinger op het canvas
          </Text>
        </View>

        {/* Tools */}
        <View className="bg-gray-800 rounded-t-3xl pt-4 pb-6 px-4">
          {/* Tool Mode Toggle */}
          <View className="flex-row justify-center mb-4">
            <Pressable
              onPress={() => setIsEraser(false)}
              className={`flex-1 mx-1 py-2 rounded-xl flex-row items-center justify-center ${
                !isEraser ? "bg-primary-600" : "bg-gray-700"
              }`}
            >
              <Ionicons name="brush" size={18} color="#FFFFFF" />
              <Text className="text-white font-medium ml-2">Penseel</Text>
            </Pressable>
            <Pressable
              onPress={() => setIsEraser(true)}
              className={`flex-1 mx-1 py-2 rounded-xl flex-row items-center justify-center ${
                isEraser ? "bg-pink-600" : "bg-gray-700"
              }`}
            >
              <Ionicons name="close-circle" size={18} color="#FFFFFF" />
              <Text className="text-white font-medium ml-2">Gum</Text>
            </Pressable>
          </View>

          {/* Brush Size */}
          <Text className="text-gray-400 text-xs mb-2 ml-1">{isEraser ? "Gum grootte" : "Penseel grootte"}</Text>
          <View className="flex-row justify-center mb-4">
            {BRUSH_SIZES.map((brush) => (
              <Pressable
                key={brush.size}
                onPress={() => setBrushSize(brush.size)}
                className={`mx-2 items-center justify-center rounded-full ${
                  brushSize === brush.size ? (isEraser ? "bg-pink-600" : "bg-primary-600") : "bg-gray-700"
                }`}
                style={{ width: 44, height: 44 }}
              >
                <View
                  style={{
                    width: isEraser ? brush.size * 1.5 : brush.size,
                    height: isEraser ? brush.size * 1.5 : brush.size,
                    borderRadius: brush.size,
                    backgroundColor: brushSize === brush.size ? "#FFFFFF" : (isEraser ? "#9CA3AF" : brushColor),
                  }}
                />
                <Text
                  className={`text-xs mt-0.5 ${
                    brushSize === brush.size ? "text-white" : "text-gray-400"
                  }`}
                >
                  {brush.label}
                </Text>
              </Pressable>
            ))}
          </View>

          {/* Color Picker - Only show when not erasing */}
          {!isEraser && (
            <>
              <Text className="text-gray-400 text-xs mb-2 ml-1">Kleur</Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                className="mb-4"
              >
                {COLORS.map((c) => (
                  <Pressable
                    key={c}
                    onPress={() => setBrushColor(c)}
                    className="mr-2"
                  >
                    <View
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 18,
                        backgroundColor: c,
                        borderWidth: brushColor === c ? 3 : 1,
                        borderColor: brushColor === c ? "#7C3AED" : "#4B5563",
                      }}
                    />
                  </Pressable>
                ))}
              </ScrollView>

              {/* Opacity Picker */}
              <Text className="text-gray-400 text-xs mb-2 ml-1">Dekking (transparantie)</Text>
              <View className="flex-row justify-center mb-4">
                {OPACITY_PRESETS.map((preset) => (
                  <Pressable
                    key={preset.value}
                    onPress={() => setBrushOpacity(preset.value)}
                    className={`mx-2 items-center justify-center rounded-xl px-3 py-2 ${
                      brushOpacity === preset.value ? "bg-primary-600" : "bg-gray-700"
                    }`}
                  >
                    <View
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 12,
                        backgroundColor: hexToRgba(brushColor, preset.value),
                        borderWidth: 1,
                        borderColor: "#4B5563",
                      }}
                    />
                    <Text
                      className={`text-xs mt-1 ${
                        brushOpacity === preset.value ? "text-white font-medium" : "text-gray-400"
                      }`}
                    >
                      {preset.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </>
          )}

          {/* Action Buttons */}
          <View className="flex-row">
            <Pressable
              onPress={onClose}
              className="flex-1 py-4 mr-2 items-center rounded-xl bg-gray-700 active:bg-gray-600"
            >
              <Text className="text-white font-semibold">Annuleren</Text>
            </Pressable>
            <Pressable
              onPress={handleSave}
              disabled={strokes.length === 0}
              className={`flex-1 py-4 ml-2 items-center rounded-xl ${
                strokes.length > 0 ? "bg-primary-600 active:bg-primary-700" : "bg-gray-600"
              }`}
            >
              <Text className="text-white font-semibold">Toevoegen ✨</Text>
            </Pressable>
          </View>
        </View>
      </SafeAreaView>
    </Modal>
  );
};

/**
 * Draggable Sticker Component with pinch-to-zoom using gesture-handler
 */
interface DraggableStickerProps {
  sticker: StickerElement;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (x: number, y: number, scale: number, rotation: number) => void;
  onDelete: () => void;
}

const DraggableSticker = ({
  sticker,
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
}: DraggableStickerProps) => {
  const [position, setPosition] = useState({ x: sticker.x, y: sticker.y });
  const [scale, setScale] = useState(sticker.scale);
  const [rotation, setRotation] = useState(sticker.rotation || 0);
  
  // Refs to track starting values
  const startPosition = useRef({ x: sticker.x, y: sticker.y });
  const startScale = useRef(sticker.scale);
  const startRotation = useRef(sticker.rotation || 0);

  // Pan gesture for dragging
  const panGesture = Gesture.Pan()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startPosition.current = { x: position.x, y: position.y };
    })
    .onUpdate((e) => {
      const newX = startPosition.current.x + e.translationX;
      const newY = startPosition.current.y + e.translationY;
      setPosition({ x: newX, y: newY });
    })
    .onEnd((e) => {
      const newX = startPosition.current.x + e.translationX;
      const newY = startPosition.current.y + e.translationY;
      onUpdate(newX, newY, scale, rotation);
    });

  // Pinch gesture for scaling
  const pinchGesture = Gesture.Pinch()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startScale.current = scale;
    })
    .onUpdate((e) => {
      const newScale = Math.min(Math.max(startScale.current * e.scale, 0.3), 4);
      setScale(newScale);
    })
    .onEnd((e) => {
      const newScale = Math.min(Math.max(startScale.current * e.scale, 0.3), 4);
      onUpdate(position.x, position.y, newScale, rotation);
    });

  // Rotation gesture
  const rotationGesture = Gesture.Rotation()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startRotation.current = rotation;
    })
    .onUpdate((e) => {
      const newRotation = startRotation.current + (e.rotation * 180 / Math.PI);
      setRotation(newRotation);
    })
    .onEnd((e) => {
      const newRotation = startRotation.current + (e.rotation * 180 / Math.PI);
      onUpdate(position.x, position.y, scale, newRotation);
    });

  // Combine gestures to work simultaneously
  const composedGesture = Gesture.Simultaneous(panGesture, pinchGesture, rotationGesture);

  const handleScaleUp = () => {
    const newScale = Math.min(scale + 0.2, 4);
    setScale(newScale);
    onUpdate(position.x, position.y, newScale, rotation);
  };

  const handleScaleDown = () => {
    const newScale = Math.max(scale - 0.2, 0.3);
    setScale(newScale);
    onUpdate(position.x, position.y, newScale, rotation);
  };

  const handleRotateLeft = () => {
    const newRotation = rotation - 15;
    setRotation(newRotation);
    onUpdate(position.x, position.y, scale, newRotation);
  };

  const handleRotateRight = () => {
    const newRotation = rotation + 15;
    setRotation(newRotation);
    onUpdate(position.x, position.y, scale, newRotation);
  };

  return (
    <GestureDetector gesture={composedGesture}>
      <View
        style={{
          position: "absolute",
          left: position.x,
          top: position.y,
          padding: 4,
          borderRadius: 8,
          borderWidth: isSelected ? 2 : 0,
          borderColor: "#7C3AED",
          borderStyle: "dashed",
          transform: [{ rotate: `${rotation}deg` }],
        }}
      >
        <Text style={{ fontSize: 50 * scale }}>{sticker.emoji}</Text>
        {isSelected && (
          <>
            {/* Delete button */}
            <Pressable
              onPress={onDelete}
              style={{
                position: "absolute",
                top: -10,
                right: -10,
                backgroundColor: "#EF4444",
                borderRadius: 12,
                width: 24,
                height: 24,
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <Ionicons name="close" size={14} color="#FFFFFF" />
            </Pressable>
            {/* Scale & Rotate controls */}
            <View
              style={{
                position: "absolute",
                bottom: -30,
                left: -20,
                right: -20,
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Pressable
                onPress={handleRotateLeft}
                style={{
                  backgroundColor: "#6366F1",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="refresh-outline" size={14} color="#FFFFFF" style={{ transform: [{ scaleX: -1 }] }} />
              </Pressable>
              <Pressable
                onPress={handleScaleDown}
                style={{
                  backgroundColor: "#374151",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="remove" size={16} color="#FFFFFF" />
              </Pressable>
              <Pressable
                onPress={handleScaleUp}
                style={{
                  backgroundColor: "#374151",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="add" size={16} color="#FFFFFF" />
              </Pressable>
              <Pressable
                onPress={handleRotateRight}
                style={{
                  backgroundColor: "#6366F1",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Ionicons name="refresh-outline" size={14} color="#FFFFFF" />
              </Pressable>
            </View>
          </>
        )}
      </View>
    </GestureDetector>
  );
};

/**
 * Draggable Text Component with pinch-to-zoom and long press to edit
 */
interface DraggableTextProps {
  textElement: TextElement;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (x: number, y: number, scale: number, rotation: number) => void;
  onDelete: () => void;
  onEdit: () => void;
}

const DraggableText = ({
  textElement,
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
  onEdit,
}: DraggableTextProps) => {
  const [position, setPosition] = useState({ x: textElement.x, y: textElement.y });
  const [scale, setScale] = useState(textElement.scale);
  const [rotation, setRotation] = useState(textElement.rotation || 0);
  
  // Refs to track starting values
  const startPosition = useRef({ x: textElement.x, y: textElement.y });
  const startScale = useRef(textElement.scale);
  const startRotation = useRef(textElement.rotation || 0);

  // Pan gesture for dragging
  const panGesture = Gesture.Pan()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startPosition.current = { x: position.x, y: position.y };
    })
    .onUpdate((e) => {
      const newX = startPosition.current.x + e.translationX;
      const newY = startPosition.current.y + e.translationY;
      setPosition({ x: newX, y: newY });
    })
    .onEnd((e) => {
      const newX = startPosition.current.x + e.translationX;
      const newY = startPosition.current.y + e.translationY;
      onUpdate(newX, newY, scale, rotation);
    });

  // Pinch gesture for scaling
  const pinchGesture = Gesture.Pinch()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startScale.current = scale;
    })
    .onUpdate((e) => {
      const newScale = Math.min(Math.max(startScale.current * e.scale, 0.3), 4);
      setScale(newScale);
    })
    .onEnd((e) => {
      const newScale = Math.min(Math.max(startScale.current * e.scale, 0.3), 4);
      onUpdate(position.x, position.y, newScale, rotation);
    });

  // Rotation gesture
  const rotationGesture = Gesture.Rotation()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startRotation.current = rotation;
    })
    .onUpdate((e) => {
      const newRotation = startRotation.current + (e.rotation * 180 / Math.PI);
      setRotation(newRotation);
    })
    .onEnd((e) => {
      const newRotation = startRotation.current + (e.rotation * 180 / Math.PI);
      onUpdate(position.x, position.y, scale, newRotation);
    });

  // Long press gesture to edit text
  const longPressGesture = Gesture.LongPress()
    .runOnJS(true)
    .minDuration(500)
    .onStart(() => {
      onEdit();
    });

  // Combine gestures - pan, pinch, rotation simultaneous, long press separate
  const composedGesture = Gesture.Race(
    longPressGesture,
    Gesture.Simultaneous(panGesture, pinchGesture, rotationGesture)
  );

  const handleScaleUp = () => {
    const newScale = Math.min(scale + 0.2, 4);
    setScale(newScale);
    onUpdate(position.x, position.y, newScale, rotation);
  };

  const handleScaleDown = () => {
    const newScale = Math.max(scale - 0.2, 0.3);
    setScale(newScale);
    onUpdate(position.x, position.y, newScale, rotation);
  };

  const handleRotateLeft = () => {
    const newRotation = rotation - 15;
    setRotation(newRotation);
    onUpdate(position.x, position.y, scale, newRotation);
  };

  const handleRotateRight = () => {
    const newRotation = rotation + 15;
    setRotation(newRotation);
    onUpdate(position.x, position.y, scale, newRotation);
  };

  return (
    <GestureDetector gesture={composedGesture}>
      <View
        style={{
          position: "absolute",
          left: position.x,
          top: position.y,
          padding: 4,
          borderRadius: 8,
          borderWidth: isSelected ? 2 : 0,
          borderColor: "#7C3AED",
          borderStyle: "dashed",
          transform: [{ rotate: `${rotation}deg` }],
        }}
      >
        <Text
          style={{
            fontSize: 24 * scale,
            fontWeight: "bold",
            color: textElement.color,
            textShadowColor: "rgba(255,255,255,0.8)",
            textShadowOffset: { width: 1, height: 1 },
            textShadowRadius: 2,
          }}
        >
          {textElement.text}
        </Text>
        {isSelected && (
          <>
            {/* Edit button */}
            <Pressable
              onPress={onEdit}
              style={{
                position: "absolute",
                top: -10,
                left: -10,
                backgroundColor: "#3B82F6",
                borderRadius: 12,
                width: 24,
                height: 24,
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <Ionicons name="pencil" size={12} color="#FFFFFF" />
            </Pressable>
            {/* Delete button */}
            <Pressable
              onPress={onDelete}
              style={{
                position: "absolute",
                top: -10,
                right: -10,
                backgroundColor: "#EF4444",
                borderRadius: 12,
                width: 24,
                height: 24,
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <Ionicons name="close" size={14} color="#FFFFFF" />
            </Pressable>
            {/* Scale & Rotate controls */}
            <View
              style={{
                position: "absolute",
                bottom: -30,
                left: -20,
                right: -20,
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Pressable
                onPress={handleRotateLeft}
                style={{
                  backgroundColor: "#6366F1",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="refresh-outline" size={14} color="#FFFFFF" style={{ transform: [{ scaleX: -1 }] }} />
              </Pressable>
              <Pressable
                onPress={handleScaleDown}
                style={{
                  backgroundColor: "#374151",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="remove" size={16} color="#FFFFFF" />
              </Pressable>
              <Pressable
                onPress={handleScaleUp}
                style={{
                  backgroundColor: "#374151",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="add" size={16} color="#FFFFFF" />
              </Pressable>
              <Pressable
                onPress={handleRotateRight}
                style={{
                  backgroundColor: "#6366F1",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Ionicons name="refresh-outline" size={14} color="#FFFFFF" />
              </Pressable>
            </View>
          </>
        )}
      </View>
    </GestureDetector>
  );
};

/**
 * Draggable Image Sticker Component (like WhatsApp stickers)
 */
interface DraggableImageStickerProps {
  imageSticker: ImageStickerElement;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (x: number, y: number, scale: number, rotation: number) => void;
  onDelete: () => void;
}

const DraggableImageSticker = ({
  imageSticker,
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
}: DraggableImageStickerProps) => {
  const [position, setPosition] = useState({ x: imageSticker.x, y: imageSticker.y });
  const [scale, setScale] = useState(imageSticker.scale);
  const [rotation, setRotation] = useState(imageSticker.rotation || 0);
  
  const startPosition = useRef({ x: imageSticker.x, y: imageSticker.y });
  const startScale = useRef(imageSticker.scale);
  const startRotation = useRef(imageSticker.rotation || 0);

  const panGesture = Gesture.Pan()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startPosition.current = { x: position.x, y: position.y };
    })
    .onUpdate((e) => {
      const newX = startPosition.current.x + e.translationX;
      const newY = startPosition.current.y + e.translationY;
      setPosition({ x: newX, y: newY });
    })
    .onEnd((e) => {
      const newX = startPosition.current.x + e.translationX;
      const newY = startPosition.current.y + e.translationY;
      onUpdate(newX, newY, scale, rotation);
    });

  const pinchGesture = Gesture.Pinch()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startScale.current = scale;
    })
    .onUpdate((e) => {
      const newScale = Math.min(Math.max(startScale.current * e.scale, 0.2), 3);
      setScale(newScale);
    })
    .onEnd((e) => {
      const newScale = Math.min(Math.max(startScale.current * e.scale, 0.2), 3);
      onUpdate(position.x, position.y, newScale, rotation);
    });

  const rotationGesture = Gesture.Rotation()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startRotation.current = rotation;
    })
    .onUpdate((e) => {
      const newRotation = startRotation.current + (e.rotation * 180 / Math.PI);
      setRotation(newRotation);
    })
    .onEnd((e) => {
      const newRotation = startRotation.current + (e.rotation * 180 / Math.PI);
      onUpdate(position.x, position.y, scale, newRotation);
    });

  const composedGesture = Gesture.Simultaneous(panGesture, pinchGesture, rotationGesture);

  const handleScaleUp = () => {
    const newScale = Math.min(scale + 0.15, 3);
    setScale(newScale);
    onUpdate(position.x, position.y, newScale, rotation);
  };

  const handleScaleDown = () => {
    const newScale = Math.max(scale - 0.15, 0.2);
    setScale(newScale);
    onUpdate(position.x, position.y, newScale, rotation);
  };

  const handleRotateLeft = () => {
    const newRotation = rotation - 15;
    setRotation(newRotation);
    onUpdate(position.x, position.y, scale, newRotation);
  };

  const handleRotateRight = () => {
    const newRotation = rotation + 15;
    setRotation(newRotation);
    onUpdate(position.x, position.y, scale, newRotation);
  };

  const size = 80 * scale;

  return (
    <GestureDetector gesture={composedGesture}>
      <View
        style={{
          position: "absolute",
          left: position.x,
          top: position.y,
          width: size,
          height: size,
          borderRadius: 8,
          borderWidth: isSelected ? 2 : 0,
          borderColor: "#7C3AED",
          borderStyle: "dashed",
          overflow: "visible",
          transform: [{ rotate: `${rotation}deg` }],
        }}
      >
        <Image
          source={{ uri: imageSticker.uri }}
          style={{ width: "100%", height: "100%", borderRadius: 6 }}
          resizeMode="cover"
        />
        {isSelected && (
          <>
            {/* Delete button */}
            <Pressable
              onPress={onDelete}
              style={{
                position: "absolute",
                top: -10,
                right: -10,
                backgroundColor: "#EF4444",
                borderRadius: 12,
                width: 24,
                height: 24,
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <Ionicons name="close" size={14} color="#FFFFFF" />
            </Pressable>
            {/* Scale & Rotate controls */}
            <View
              style={{
                position: "absolute",
                bottom: -30,
                left: -20,
                right: -20,
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Pressable
                onPress={handleRotateLeft}
                style={{
                  backgroundColor: "#6366F1",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="refresh-outline" size={14} color="#FFFFFF" style={{ transform: [{ scaleX: -1 }] }} />
              </Pressable>
              <Pressable
                onPress={handleScaleDown}
                style={{
                  backgroundColor: "#374151",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="remove" size={16} color="#FFFFFF" />
              </Pressable>
              <Pressable
                onPress={handleScaleUp}
                style={{
                  backgroundColor: "#374151",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="add" size={16} color="#FFFFFF" />
              </Pressable>
              <Pressable
                onPress={handleRotateRight}
                style={{
                  backgroundColor: "#6366F1",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Ionicons name="refresh-outline" size={14} color="#FFFFFF" />
              </Pressable>
            </View>
          </>
        )}
      </View>
    </GestureDetector>
  );
};

/**
 * Draggable Shape Component
 */
interface DraggableShapeProps {
  shapeElement: ShapeElement;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (x: number, y: number, scale: number, rotation: number) => void;
  onDelete: () => void;
}

const DraggableShape = ({
  shapeElement,
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
}: DraggableShapeProps) => {
  const [position, setPosition] = useState({ x: shapeElement.x, y: shapeElement.y });
  const [scale, setScale] = useState(shapeElement.scale);
  const [rotation, setRotation] = useState(shapeElement.rotation || 0);
  
  const startPosition = useRef({ x: shapeElement.x, y: shapeElement.y });
  const startScale = useRef(shapeElement.scale);
  const startRotation = useRef(shapeElement.rotation || 0);

  const panGesture = Gesture.Pan()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startPosition.current = { x: position.x, y: position.y };
    })
    .onUpdate((e) => {
      const newX = startPosition.current.x + e.translationX;
      const newY = startPosition.current.y + e.translationY;
      setPosition({ x: newX, y: newY });
    })
    .onEnd((e) => {
      const newX = startPosition.current.x + e.translationX;
      const newY = startPosition.current.y + e.translationY;
      onUpdate(newX, newY, scale, rotation);
    });

  const pinchGesture = Gesture.Pinch()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startScale.current = scale;
    })
    .onUpdate((e) => {
      const newScale = Math.min(Math.max(startScale.current * e.scale, 0.2), 4);
      setScale(newScale);
    })
    .onEnd((e) => {
      const newScale = Math.min(Math.max(startScale.current * e.scale, 0.2), 4);
      onUpdate(position.x, position.y, newScale, rotation);
    });

  const rotationGesture = Gesture.Rotation()
    .runOnJS(true)
    .onStart(() => {
      onSelect();
      startRotation.current = rotation;
    })
    .onUpdate((e) => {
      const newRotation = startRotation.current + (e.rotation * 180 / Math.PI);
      setRotation(newRotation);
    })
    .onEnd((e) => {
      const newRotation = startRotation.current + (e.rotation * 180 / Math.PI);
      onUpdate(position.x, position.y, scale, newRotation);
    });

  const composedGesture = Gesture.Simultaneous(panGesture, pinchGesture, rotationGesture);

  const handleScaleUp = () => {
    const newScale = Math.min(scale + 0.2, 4);
    setScale(newScale);
    onUpdate(position.x, position.y, newScale, rotation);
  };

  const handleScaleDown = () => {
    const newScale = Math.max(scale - 0.2, 0.2);
    setScale(newScale);
    onUpdate(position.x, position.y, newScale, rotation);
  };

  const handleRotateLeft = () => {
    const newRotation = rotation - 15;
    setRotation(newRotation);
    onUpdate(position.x, position.y, scale, newRotation);
  };

  const handleRotateRight = () => {
    const newRotation = rotation + 15;
    setRotation(newRotation);
    onUpdate(position.x, position.y, scale, newRotation);
  };

  const size = 60 * scale;

  /**
   * Render the shape based on type
   */
  const renderShape = () => {
    const baseStyle = { width: size, height: size };
    
    switch (shapeElement.shape) {
      case 'circle':
        return (
          <View
            style={[baseStyle, {
              borderRadius: size / 2,
              backgroundColor: shapeElement.color,
            }]}
          />
        );
      case 'square':
        return (
          <View
            style={[baseStyle, {
              borderRadius: size * 0.1,
              backgroundColor: shapeElement.color,
            }]}
          />
        );
      case 'heart':
        return (
          <Text style={{ fontSize: size * 0.9, color: shapeElement.color }}>♥</Text>
        );
      case 'star':
        return (
          <Text style={{ fontSize: size * 0.9, color: shapeElement.color }}>★</Text>
        );
      case 'triangle':
        return (
          <View style={{ width: 0, height: 0 }}>
            <View
              style={{
                width: 0,
                height: 0,
                borderLeftWidth: size / 2,
                borderRightWidth: size / 2,
                borderBottomWidth: size,
                borderLeftColor: 'transparent',
                borderRightColor: 'transparent',
                borderBottomColor: shapeElement.color,
              }}
            />
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <GestureDetector gesture={composedGesture}>
      <View
        style={{
          position: "absolute",
          left: position.x,
          top: position.y,
          padding: 4,
          borderRadius: 8,
          borderWidth: isSelected ? 2 : 0,
          borderColor: "#7C3AED",
          borderStyle: "dashed",
          alignItems: 'center',
          justifyContent: 'center',
          transform: [{ rotate: `${rotation}deg` }],
        }}
      >
        {renderShape()}
        {isSelected && (
          <>
            {/* Delete button */}
            <Pressable
              onPress={onDelete}
              style={{
                position: "absolute",
                top: -10,
                right: -10,
                backgroundColor: "#EF4444",
                borderRadius: 12,
                width: 24,
                height: 24,
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <Ionicons name="close" size={14} color="#FFFFFF" />
            </Pressable>
            {/* Scale & Rotate controls */}
            <View
              style={{
                position: "absolute",
                bottom: -30,
                left: -20,
                right: -20,
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Pressable
                onPress={handleRotateLeft}
                style={{
                  backgroundColor: "#6366F1",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="refresh-outline" size={14} color="#FFFFFF" style={{ transform: [{ scaleX: -1 }] }} />
              </Pressable>
              <Pressable
                onPress={handleScaleDown}
                style={{
                  backgroundColor: "#374151",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="remove" size={16} color="#FFFFFF" />
              </Pressable>
              <Pressable
                onPress={handleScaleUp}
                style={{
                  backgroundColor: "#374151",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 4,
                }}
              >
                <Ionicons name="add" size={16} color="#FFFFFF" />
              </Pressable>
              <Pressable
                onPress={handleRotateRight}
                style={{
                  backgroundColor: "#6366F1",
                  borderRadius: 12,
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Ionicons name="refresh-outline" size={14} color="#FFFFFF" />
              </Pressable>
            </View>
          </>
        )}
      </View>
    </GestureDetector>
  );
};

/**
 * Avatar Creator Screen - Enhanced with Layer Controls
 */
export default function AvatarCreatorScreen() {
  const router = useRouter();
  const { id: teamId, weekendId, color } = useLocalSearchParams<{
    id: string;
    weekendId: string;
    color: string;
  }>();
  const { user } = useAuth();
  const viewShotRef = useRef<ViewShot>(null);

  // State - unified elements array with z-index ordering
  const [backgroundColor, setBackgroundColor] = useState(color || "#E0E7FF");
  const [backgroundGradient, setBackgroundGradient] = useState<string[] | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [elements, setElements] = useState<CanvasElement[]>([]);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState("#1F2937");
  const [nextZIndex, setNextZIndex] = useState(1);

  // Team images from database
  const [teamImages, setTeamImages] = useState<string[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);

  // Edit/Create modal state
  const [showStartModal, setShowStartModal] = useState(false);
  const [existingDesign, setExistingDesign] = useState<AvatarDesign | null>(null);
  const [existingAvatarUrl, setExistingAvatarUrl] = useState<string | null>(null);
  const [isLoadingDesign, setIsLoadingDesign] = useState(true);

  // UI state
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [showStickerPackPicker, setShowStickerPackPicker] = useState(false);
  const [showShapePicker, setShowShapePicker] = useState(false);
  const [showDrawingModal, setShowDrawingModal] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [showEditTextModal, setShowEditTextModal] = useState(false);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [editTextValue, setEditTextValue] = useState("");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [newTextValue, setNewTextValue] = useState("");
  const [stickerCategory, setStickerCategory] = useState(0);
  const [selectedStickerPack, setSelectedStickerPack] = useState<StickerPack | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // History for undo
  const [history, setHistory] = useState<CanvasElement[][]>([]);

  // Get selected element
  const selectedElement = elements.find(e => e.id === selectedElementId);

  // Get elements sorted by z-index for rendering
  const sortedElements = [...elements].sort((a, b) => a.zIndex - b.zIndex);

  /**
   * Fetch existing design and avatar from database on mount
   */
  useEffect(() => {
    const fetchExistingDesign = async () => {
      if (!teamId) {
        setIsLoadingDesign(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("teams")
          .select("avatar_url, avatar_design")
          .eq("id", teamId)
          .single();

        if (error) throw error;

        if (data?.avatar_url) {
          setExistingAvatarUrl(data.avatar_url);
        }
        
        if (data?.avatar_design) {
          setExistingDesign(data.avatar_design as AvatarDesign);
        }

        // Show start modal if there's an existing avatar or design
        if (data?.avatar_url || data?.avatar_design) {
          setShowStartModal(true);
        }
      } catch (error) {
        console.error("Error fetching existing design:", error);
      } finally {
        setIsLoadingDesign(false);
      }
    };

    fetchExistingDesign();
  }, [teamId]);

  /**
   * Fetch team images from storage on mount
   */
  useEffect(() => {
    const fetchTeamImages = async () => {
      if (!weekendId || !teamId) {
        setIsLoadingImages(false);
        return;
      }

      try {
        // Fetch from team uploads folder
        const { data: uploadsData } = await supabase.storage
          .from("team-assets")
          .list(`${weekendId}/${teamId}/uploads`, {
            limit: 20,
            sortBy: { column: "created_at", order: "desc" },
          });

        const images: string[] = [];

        if (uploadsData && uploadsData.length > 0) {
          uploadsData
            .filter((file) => file.name !== ".emptyFolderPlaceholder")
            .forEach((file) => {
              const { data: urlData } = supabase.storage
                .from("team-assets")
                .getPublicUrl(`${weekendId}/${teamId}/uploads/${file.name}`);
              images.push(urlData.publicUrl);
            });
        }

        setTeamImages(images);
      } catch (error) {
        console.error("Error fetching team images:", error);
      } finally {
        setIsLoadingImages(false);
      }
    };

    fetchTeamImages();
  }, [weekendId, teamId]);

  /**
   * Load existing design into canvas
   */
  const handleLoadExistingDesign = () => {
    if (!existingDesign) return;
    
    setBackgroundColor(existingDesign.backgroundColor);
    setBackgroundGradient(existingDesign.backgroundGradient);
    setBackgroundImage(existingDesign.backgroundImage);
    setElements(existingDesign.elements);
    setNextZIndex(existingDesign.nextZIndex);
    setShowStartModal(false);
  };

  /**
   * Start with a fresh canvas
   */
  const handleStartNew = () => {
    setElements([]);
    setBackgroundColor(color || "#E0E7FF");
    setBackgroundGradient(null);
    setBackgroundImage(null);
    setNextZIndex(1);
    setShowStartModal(false);
  };

  /**
   * Save current state to history
   */
  const saveToHistory = useCallback(() => {
    setHistory((prev) => [...prev.slice(-19), [...elements]]);
  }, [elements]);

  /**
   * Undo last action
   */
  const handleUndo = () => {
    if (history.length === 0) return;
    const lastState = history[history.length - 1];
    setElements(lastState);
    setHistory((prev) => prev.slice(0, -1));
  };

  /**
   * Clear canvas
   */
  const handleClear = () => {
    Alert.alert("Wis alles", "Weet je zeker dat je alles wilt wissen?", [
      { text: "Annuleer", style: "cancel" },
      {
        text: "Wissen",
        style: "destructive",
        onPress: () => {
          saveToHistory();
          setElements([]);
          setBackgroundImage(null);
          setBackgroundGradient(null);
          setNextZIndex(1);
        },
      },
    ]);
  };

  /**
   * Layer control: Bring element to front
   */
  const handleBringToFront = () => {
    if (!selectedElementId) return;
    saveToHistory();
    const maxZ = Math.max(...elements.map(e => e.zIndex), 0);
    setElements(prev => prev.map(e => 
      e.id === selectedElementId ? { ...e, zIndex: maxZ + 1 } : e
    ));
    setNextZIndex(maxZ + 2);
  };

  /**
   * Layer control: Send element to back
   */
  const handleSendToBack = () => {
    if (!selectedElementId) return;
    saveToHistory();
    const minZ = Math.min(...elements.map(e => e.zIndex), 0);
    setElements(prev => prev.map(e => 
      e.id === selectedElementId ? { ...e, zIndex: minZ - 1 } : e
    ));
  };

  /**
   * Layer control: Move element up one layer
   */
  const handleMoveUp = () => {
    if (!selectedElementId) return;
    const currentElement = elements.find(e => e.id === selectedElementId);
    if (!currentElement) return;
    
    // Find the element just above this one
    const elementsAbove = elements.filter(e => e.zIndex > currentElement.zIndex);
    if (elementsAbove.length === 0) return; // Already at top
    
    const nextAbove = elementsAbove.reduce((closest, e) => 
      e.zIndex < closest.zIndex ? e : closest
    );
    
    saveToHistory();
    setElements(prev => prev.map(e => {
      if (e.id === selectedElementId) return { ...e, zIndex: nextAbove.zIndex };
      if (e.id === nextAbove.id) return { ...e, zIndex: currentElement.zIndex };
      return e;
    }));
  };

  /**
   * Layer control: Move element down one layer
   */
  const handleMoveDown = () => {
    if (!selectedElementId) return;
    const currentElement = elements.find(e => e.id === selectedElementId);
    if (!currentElement) return;
    
    // Find the element just below this one
    const elementsBelow = elements.filter(e => e.zIndex < currentElement.zIndex);
    if (elementsBelow.length === 0) return; // Already at bottom
    
    const nextBelow = elementsBelow.reduce((closest, e) => 
      e.zIndex > closest.zIndex ? e : closest
    );
    
    saveToHistory();
    setElements(prev => prev.map(e => {
      if (e.id === selectedElementId) return { ...e, zIndex: nextBelow.zIndex };
      if (e.id === nextBelow.id) return { ...e, zIndex: currentElement.zIndex };
      return e;
    }));
  };

  /**
   * Pick new image from library
   */
  const handlePickNewImage = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        Alert.alert("Geen toegang", "We hebben toegang nodig tot je foto's.");
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        setBackgroundImage(result.assets[0].uri);
        setShowImagePicker(false);
      }
    } catch (error) {
      console.error("Error picking image:", error);
      Alert.alert("Fout", "Kon afbeelding niet laden");
    }
  };

  /**
   * Select existing team image
   */
  const handleSelectTeamImage = (imageUrl: string) => {
    setBackgroundImage(imageUrl);
    setShowImagePicker(false);
  };

  /**
   * Add sticker to canvas
   */
  const handleAddSticker = (emoji: string) => {
    saveToHistory();
    const newSticker: StickerElement = {
      id: `sticker-${Date.now()}`,
      type: 'sticker',
      emoji,
      x: CANVAS_SIZE / 2 - 25,
      y: CANVAS_SIZE / 2 - 25,
      scale: 1,
      rotation: 0,
      zIndex: nextZIndex,
    };
    setElements((prev) => [...prev, newSticker]);
    setNextZIndex(prev => prev + 1);
    setSelectedElementId(newSticker.id);
    setShowStickerPicker(false);
  };

  /**
   * Add text to canvas
   */
  const handleAddText = () => {
    if (!newTextValue.trim()) return;
    saveToHistory();
    const newText: TextElement = {
      id: `text-${Date.now()}`,
      type: 'text',
      text: newTextValue.trim(),
      x: CANVAS_SIZE / 2 - 40,
      y: CANVAS_SIZE / 2 - 15,
      scale: 1,
      rotation: 0,
      color: selectedColor,
      zIndex: nextZIndex,
    };
    setElements((prev) => [...prev, newText]);
    setNextZIndex(prev => prev + 1);
    setSelectedElementId(newText.id);
    setShowTextModal(false);
    setNewTextValue("");
  };

  /**
   * Add shape to canvas
   */
  const handleAddShape = (shape: ShapeElement['shape']) => {
    saveToHistory();
    const newShape: ShapeElement = {
      id: `shape-${Date.now()}`,
      type: 'shape',
      shape,
      x: CANVAS_SIZE / 2 - 30,
      y: CANVAS_SIZE / 2 - 30,
      scale: 1,
      rotation: 0,
      color: selectedColor,
      zIndex: nextZIndex,
    };
    setElements((prev) => [...prev, newShape]);
    setNextZIndex(prev => prev + 1);
    setSelectedElementId(newShape.id);
    setShowShapePicker(false);
  };

  /**
   * Update element position, scale, and rotation
   */
  const handleUpdateElement = (id: string, x: number, y: number, scale: number, rotation: number) => {
    setElements((prev) =>
      prev.map((e) => (e.id === id ? { ...e, x, y, scale, rotation } : e))
    );
  };

  /**
   * Delete element
   */
  const handleDeleteElement = (id: string) => {
    saveToHistory();
    setElements((prev) => prev.filter((e) => e.id !== id));
    setSelectedElementId(null);
  };

  /**
   * Open edit modal for text
   */
  const handleEditText = (textId: string) => {
    const textElement = elements.find((e) => e.id === textId && e.type === 'text') as TextElement | undefined;
    if (textElement) {
      setEditingTextId(textId);
      setEditTextValue(textElement.text);
      setSelectedColor(textElement.color);
      setShowEditTextModal(true);
    }
  };

  /**
   * Save edited text
   */
  const handleSaveEditText = () => {
    if (!editingTextId || !editTextValue.trim()) return;
    saveToHistory();
    setElements((prev) =>
      prev.map((e) =>
        e.id === editingTextId && e.type === 'text'
          ? { ...e, text: editTextValue.trim(), color: selectedColor }
          : e
      )
    );
    setShowEditTextModal(false);
    setEditingTextId(null);
    setEditTextValue("");
  };

  /**
   * Add image sticker from library
   */
  const handleAddImageSticker = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        Alert.alert("Geen toegang", "We hebben toegang nodig tot je foto's.");
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        saveToHistory();
        const newImageSticker: ImageStickerElement = {
          id: `img-${Date.now()}`,
          type: 'image',
          uri: result.assets[0].uri,
          x: CANVAS_SIZE / 2 - 40,
          y: CANVAS_SIZE / 2 - 40,
          scale: 1,
          rotation: 0,
          zIndex: nextZIndex,
        };
        setElements((prev) => [...prev, newImageSticker]);
        setNextZIndex(prev => prev + 1);
        setSelectedElementId(newImageSticker.id);
      }
    } catch (error) {
      console.error("Error picking image:", error);
      Alert.alert("Fout", "Kon afbeelding niet laden");
    }
  };

  /**
   * Add sticker from bundled sticker pack
   */
  const handleAddPackSticker = (sticker: PackSticker) => {
    saveToHistory();
    
    // Resolve the require() source to get the asset info
    const resolvedAsset = Image.resolveAssetSource(sticker.source);
    
    const newImageSticker: ImageStickerElement = {
      id: `pack-${Date.now()}`,
      type: 'image',
      uri: resolvedAsset.uri,
      x: CANVAS_SIZE / 2 - 40,
      y: CANVAS_SIZE / 2 - 40,
      scale: 1,
      rotation: 0,
      zIndex: nextZIndex,
    };
    setElements((prev) => [...prev, newImageSticker]);
    setNextZIndex(prev => prev + 1);
    setSelectedElementId(newImageSticker.id);
    setShowStickerPackPicker(false);
    setSelectedStickerPack(null);
  };

  /**
   * Add drawing as image sticker
   */
  const handleSaveDrawing = (imageUri: string) => {
    saveToHistory();
    const newDrawing: ImageStickerElement = {
      id: `drawing-${Date.now()}`,
      type: 'image',
      uri: imageUri,
      x: CANVAS_SIZE / 2 - 50,
      y: CANVAS_SIZE / 2 - 50,
      scale: 1,
      rotation: 0,
      zIndex: nextZIndex,
    };
    setElements((prev) => [...prev, newDrawing]);
    setNextZIndex(prev => prev + 1);
    setSelectedElementId(newDrawing.id);
    setShowDrawingModal(false);
  };

  /**
   * Deselect when tapping canvas background
   */
  const handleCanvasTap = () => {
    setSelectedElementId(null);
  };

  /**
   * Save avatar (both image and design JSON)
   */
  const handleSave = async () => {
    if (!teamId || !weekendId || !user) return;

    setIsSaving(true);
    setSelectedElementId(null);

    try {
      await new Promise((resolve) => setTimeout(resolve, 150));

      const uri = await viewShotRef.current?.capture?.();
      if (!uri) throw new Error("Could not capture canvas");

      const base64 = await readAsStringAsync(uri, { encoding: "base64" });
      const fileName = `${weekendId}/${teamId}/avatar.png`;

      const { error: uploadError } = await supabase.storage
        .from("team-assets")
        .upload(fileName, decode(base64), {
          contentType: "image/png",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("team-assets")
        .getPublicUrl(fileName);

      // Create the design JSON to save for editing later
      const avatarDesign: AvatarDesign = {
        version: 1,
        backgroundColor,
        backgroundGradient,
        backgroundImage,
        elements,
        nextZIndex,
      };

      const { error: updateError } = await supabase
        .from("teams")
        .update({ 
          avatar_url: `${urlData.publicUrl}?t=${Date.now()}`,
          avatar_design: avatarDesign,
        })
        .eq("id", teamId);

      if (updateError) throw updateError;

      Alert.alert("Opgeslagen! 🎨", "Je avatar is opgeslagen!", [
        { text: "OK", onPress: () => router.back() },
      ]);
    } catch (error: any) {
      console.error("Error saving avatar:", error);
      Alert.alert("Fout", error.message || "Kon avatar niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state
  if (isLoadingDesign) {
    return (
      <GestureHandlerRootView style={{ flex: 1 }}>
        <SafeAreaView className="flex-1 bg-gray-900 items-center justify-center" edges={["top"]}>
          <ActivityIndicator size="large" color="#7C3AED" />
          <Text className="text-gray-400 mt-4">Laden...</Text>
        </SafeAreaView>
      </GestureHandlerRootView>
    );
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaView className="flex-1 bg-gray-900" edges={["top"]}>
        {/* Start Modal - Edit existing or create new */}
        <Modal visible={showStartModal} animationType="fade" transparent>
          <View className="flex-1 bg-black/70 items-center justify-center px-6">
            <View className="bg-gray-800 rounded-2xl p-6 w-full max-w-sm">
              <Text className="text-xl font-bold text-white text-center mb-2">
                Avatar Studio 🎨
              </Text>
              <Text className="text-gray-400 text-center mb-6">
                Je hebt al een bestaand ontwerp. Wat wil je doen?
              </Text>

              {/* Existing Avatar Preview */}
              {existingAvatarUrl && (
                <View className="items-center mb-6">
                  <View className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-600">
                    <Image
                      source={{ uri: existingAvatarUrl }}
                      style={{ width: "100%", height: "100%" }}
                      resizeMode="cover"
                    />
                  </View>
                  <Text className="text-gray-500 text-xs mt-2">Huidige avatar</Text>
                </View>
              )}

              {/* Edit Existing Button */}
              {existingDesign && (
                <Pressable
                  onPress={handleLoadExistingDesign}
                  className="bg-primary-600 rounded-xl py-4 mb-3 flex-row items-center justify-center active:bg-primary-700"
                >
                  <Ionicons name="pencil" size={20} color="#FFFFFF" />
                  <Text className="text-white font-semibold ml-2">Bewerk bestaande</Text>
                </Pressable>
              )}

              {/* Start New Button */}
              <Pressable
                onPress={handleStartNew}
                className="bg-gray-700 rounded-xl py-4 mb-3 flex-row items-center justify-center active:bg-gray-600"
              >
                <Ionicons name="add-circle" size={20} color="#FFFFFF" />
                <Text className="text-white font-semibold ml-2">Begin opnieuw</Text>
              </Pressable>

              {/* Cancel Button */}
              <Pressable
                onPress={() => router.back()}
                className="py-3 items-center"
              >
                <Text className="text-gray-400 font-medium">Annuleren</Text>
              </Pressable>
            </View>
          </View>
        </Modal>

        {/* Header */}
      <View className="flex-row items-center justify-between px-4 py-3">
        <Pressable onPress={() => router.back()} className="p-2">
          <Ionicons name="close" size={28} color="#FFFFFF" />
        </Pressable>
        <Text className="text-lg font-bold text-white">Avatar Creator</Text>
        <View className="flex-row items-center">
          <Pressable
            onPress={handleUndo}
            disabled={history.length === 0}
            className="p-2 mr-2"
          >
            <Ionicons
              name="arrow-undo"
              size={24}
              color={history.length === 0 ? "#6B7280" : "#FFFFFF"}
            />
          </Pressable>
          <Pressable onPress={handleClear} className="p-2">
            <Ionicons name="trash-outline" size={24} color="#EF4444" />
          </Pressable>
        </View>
      </View>

      {/* Canvas Area */}
      <View className="flex-1 items-center justify-center px-6">
        <ViewShot
          ref={viewShotRef}
          options={{ format: "png", quality: 1 }}
          style={{
            width: CANVAS_SIZE,
            height: CANVAS_SIZE,
            borderRadius: CANVAS_SIZE / 2,
            overflow: "hidden",
          }}
        >
          <Pressable
            onPress={handleCanvasTap}
            style={{
              width: CANVAS_SIZE,
              height: CANVAS_SIZE,
              backgroundColor: backgroundGradient ? 'transparent' : backgroundColor,
              borderRadius: CANVAS_SIZE / 2,
              overflow: "hidden",
            }}
          >
            {/* Background Gradient */}
            {backgroundGradient && !backgroundImage && (
              <LinearGradient
                colors={backgroundGradient as [string, string, ...string[]]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                }}
              />
            )}
            {/* Background Image */}
            {backgroundImage && (
              <Image
                source={{ uri: backgroundImage }}
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                }}
                resizeMode="cover"
              />
            )}

            {/* Render all elements sorted by z-index */}
            {sortedElements.map((element) => {
              switch (element.type) {
                case 'sticker':
                  return (
                    <DraggableSticker
                      key={element.id}
                      sticker={element}
                      isSelected={selectedElementId === element.id}
                      onSelect={() => setSelectedElementId(element.id)}
                      onUpdate={(x, y, scale, rotation) => handleUpdateElement(element.id, x, y, scale, rotation)}
                      onDelete={() => handleDeleteElement(element.id)}
                    />
                  );
                case 'text':
                  return (
                    <DraggableText
                      key={element.id}
                      textElement={element}
                      isSelected={selectedElementId === element.id}
                      onSelect={() => setSelectedElementId(element.id)}
                      onUpdate={(x, y, scale, rotation) => handleUpdateElement(element.id, x, y, scale, rotation)}
                      onDelete={() => handleDeleteElement(element.id)}
                      onEdit={() => handleEditText(element.id)}
                    />
                  );
                case 'image':
                  return (
                    <DraggableImageSticker
                      key={element.id}
                      imageSticker={element}
                      isSelected={selectedElementId === element.id}
                      onSelect={() => setSelectedElementId(element.id)}
                      onUpdate={(x, y, scale, rotation) => handleUpdateElement(element.id, x, y, scale, rotation)}
                      onDelete={() => handleDeleteElement(element.id)}
                    />
                  );
                case 'shape':
                  return (
                    <DraggableShape
                      key={element.id}
                      shapeElement={element}
                      isSelected={selectedElementId === element.id}
                      onSelect={() => setSelectedElementId(element.id)}
                      onUpdate={(x, y, scale, rotation) => handleUpdateElement(element.id, x, y, scale, rotation)}
                      onDelete={() => handleDeleteElement(element.id)}
                    />
                  );
                default:
                  return null;
              }
            })}
          </Pressable>
        </ViewShot>

        {/* Hint */}
        <Text className="text-gray-500 text-xs mt-3 text-center">
          Sleep om te verplaatsen • Knijp om te schalen • Draai met 2 vingers
        </Text>
      </View>

      {/* Layer Controls - Show when element is selected */}
      {selectedElement && (
        <View className="bg-gray-800 px-4 py-3 border-b border-gray-700">
          <Text className="text-gray-400 text-xs mb-2 text-center">Laag Volgorde</Text>
          <View className="flex-row justify-center items-center">
            <Pressable
              onPress={handleSendToBack}
              className="bg-gray-700 px-3 py-2 rounded-lg mr-2 flex-row items-center active:bg-gray-600"
              accessibilityLabel="Naar achteren"
            >
              <Ionicons name="layers-outline" size={16} color="#FFFFFF" />
              <Ionicons name="arrow-down" size={14} color="#FFFFFF" style={{ marginLeft: 2 }} />
              <Text className="text-white text-xs ml-1">Achter</Text>
            </Pressable>
            
            <Pressable
              onPress={handleMoveDown}
              className="bg-gray-700 p-2 rounded-lg mr-1 active:bg-gray-600"
              accessibilityLabel="Eén laag naar beneden"
            >
              <Ionicons name="chevron-down" size={20} color="#FFFFFF" />
            </Pressable>
            
            <View className="bg-gray-600 px-3 py-1 rounded-lg mx-1">
              <Text className="text-gray-300 text-xs">
                {sortedElements.findIndex(e => e.id === selectedElementId) + 1}/{elements.length}
              </Text>
            </View>
            
            <Pressable
              onPress={handleMoveUp}
              className="bg-gray-700 p-2 rounded-lg ml-1 active:bg-gray-600"
              accessibilityLabel="Eén laag naar boven"
            >
              <Ionicons name="chevron-up" size={20} color="#FFFFFF" />
            </Pressable>
            
            <Pressable
              onPress={handleBringToFront}
              className="bg-gray-700 px-3 py-2 rounded-lg ml-2 flex-row items-center active:bg-gray-600"
              accessibilityLabel="Naar voren"
            >
              <Text className="text-white text-xs mr-1">Voor</Text>
              <Ionicons name="layers-outline" size={16} color="#FFFFFF" />
              <Ionicons name="arrow-up" size={14} color="#FFFFFF" style={{ marginLeft: 2 }} />
            </Pressable>
          </View>
        </View>
      )}

      {/* Tools Bar */}
      <View className="bg-gray-800 rounded-t-3xl pt-4 pb-2 px-4">
        {/* Tool Buttons - Row 1 */}
        <View className="flex-row justify-center mb-2">
          <Pressable
            onPress={() => setShowImagePicker(true)}
            className="bg-gray-700 px-3 py-2 rounded-full mr-2 flex-row items-center active:bg-gray-600"
          >
            <Ionicons name="image-outline" size={18} color="#FFFFFF" />
            <Text className="text-white ml-1 text-sm font-medium">Achtergrond</Text>
          </Pressable>

          <Pressable
            onPress={() => setShowStickerPicker(true)}
            className="bg-gray-700 px-3 py-2 rounded-full mr-2 flex-row items-center active:bg-gray-600"
          >
            <Text className="text-base mr-1">😀</Text>
            <Text className="text-white text-sm font-medium">Emoji</Text>
          </Pressable>

          <Pressable
            onPress={() => setShowStickerPackPicker(true)}
            className="bg-teal-600 px-3 py-2 rounded-full mr-2 flex-row items-center active:bg-teal-700"
          >
            <Ionicons name="images" size={18} color="#FFFFFF" />
            <Text className="text-white ml-1 text-sm font-medium">Stickers</Text>
          </Pressable>
        </View>

        {/* Tool Buttons - Row 2 */}
        <View className="flex-row justify-center mb-2">
          <Pressable
            onPress={() => setShowShapePicker(true)}
            className="bg-blue-600 px-3 py-2 rounded-full mr-2 flex-row items-center active:bg-blue-700"
          >
            <Ionicons name="shapes-outline" size={18} color="#FFFFFF" />
            <Text className="text-white ml-1 text-sm font-medium">Vormen</Text>
          </Pressable>

          <Pressable
            onPress={handleAddImageSticker}
            className="bg-purple-600 px-3 py-2 rounded-full mr-2 flex-row items-center active:bg-purple-700"
          >
            <Ionicons name="cut-outline" size={18} color="#FFFFFF" />
            <Text className="text-white ml-1 text-sm font-medium">Foto Sticker</Text>
          </Pressable>

          <Pressable
            onPress={() => setShowDrawingModal(true)}
            className="bg-pink-600 px-3 py-2 rounded-full mr-2 flex-row items-center active:bg-pink-700"
          >
            <Ionicons name="brush-outline" size={18} color="#FFFFFF" />
            <Text className="text-white ml-1 text-sm font-medium">Tekenen</Text>
          </Pressable>

          <Pressable
            onPress={() => setShowTextModal(true)}
            className="bg-gray-700 px-3 py-2 rounded-full mr-2 flex-row items-center active:bg-gray-600"
          >
            <Ionicons name="text" size={18} color="#FFFFFF" />
            <Text className="text-white ml-1 text-sm font-medium">Tekst</Text>
          </Pressable>
        </View>

        {/* Tool Buttons - Row 3 */}
        <View className="flex-row justify-center mb-4">
          <Pressable
            onPress={() => setShowColorPicker(true)}
            className="bg-gray-700 px-3 py-2 rounded-full flex-row items-center active:bg-gray-600"
          >
            <View
              style={{
                width: 18,
                height: 18,
                borderRadius: 9,
                backgroundColor: backgroundColor,
                borderWidth: 2,
                borderColor: "#FFFFFF",
              }}
            />
            <Text className="text-white ml-1 text-sm font-medium">Kleur</Text>
          </Pressable>
        </View>

        {/* Save Button */}
        <Pressable
          onPress={handleSave}
          disabled={isSaving}
          className={`rounded-xl py-4 items-center ${
            isSaving ? "bg-gray-600" : "bg-primary-600 active:bg-primary-700"
          }`}
        >
          {isSaving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text className="text-white font-bold text-lg">Opslaan</Text>
          )}
        </Pressable>
      </View>

      {/* Image Picker Modal */}
      <Modal visible={showImagePicker} animationType="slide" transparent>
        <View className="flex-1 bg-black/50 justify-end">
          <View className="bg-gray-800 rounded-t-3xl pt-4 pb-8 px-4" style={{ maxHeight: "70%" }}>
            <View className="flex-row items-center justify-between mb-4">
              <Text className="text-lg font-bold text-white">Kies een Afbeelding</Text>
              <Pressable onPress={() => setShowImagePicker(false)}>
                <Ionicons name="close" size={24} color="#9CA3AF" />
              </Pressable>
            </View>

            {/* Upload new image button */}
            <Pressable
              onPress={handlePickNewImage}
              className="bg-primary-600 rounded-xl py-4 mb-4 flex-row items-center justify-center active:bg-primary-700"
            >
              <Ionicons name="cloud-upload-outline" size={24} color="#FFFFFF" />
              <Text className="text-white font-semibold ml-2">Nieuwe Foto Uploaden</Text>
            </Pressable>

            {/* Team images */}
            {isLoadingImages ? (
              <View className="py-8 items-center">
                <ActivityIndicator color="#7C3AED" />
                <Text className="text-gray-400 mt-2">Afbeeldingen laden...</Text>
              </View>
            ) : teamImages.length > 0 ? (
              <>
                <Text className="text-gray-400 text-sm mb-3">Team Afbeeldingen</Text>
                <ScrollView showsVerticalScrollIndicator={false}>
                  <View className="flex-row flex-wrap">
                    {teamImages.map((imageUrl, index) => (
                      <Pressable
                        key={index}
                        onPress={() => handleSelectTeamImage(imageUrl)}
                        className="w-[30%] aspect-square m-[1.5%] rounded-xl overflow-hidden border-2 border-gray-700 active:border-primary-500"
                      >
                        <Image
                          source={{ uri: imageUrl }}
                          style={{ width: "100%", height: "100%" }}
                          resizeMode="cover"
                        />
                      </Pressable>
                    ))}
                  </View>
                </ScrollView>
              </>
            ) : (
              <View className="py-8 items-center">
                <Ionicons name="images-outline" size={48} color="#6B7280" />
                <Text className="text-gray-400 mt-2 text-center">
                  Nog geen team afbeeldingen{"\n"}Upload er een om te beginnen!
                </Text>
              </View>
            )}

            {/* Remove background option */}
            {backgroundImage && (
              <Pressable
                onPress={() => {
                  setBackgroundImage(null);
                  setShowImagePicker(false);
                }}
                className="mt-4 py-3 items-center border border-red-500 rounded-xl"
              >
                <Text className="text-red-500 font-medium">Achtergrond Verwijderen</Text>
              </Pressable>
            )}
          </View>
        </View>
      </Modal>

      {/* Sticker Picker Modal */}
      <Modal visible={showStickerPicker} animationType="slide" transparent>
        <View className="flex-1 bg-black/50 justify-end">
          <View className="bg-gray-800 rounded-t-3xl pt-4 pb-8 px-4" style={{ maxHeight: "70%" }}>
            <View className="flex-row items-center justify-between mb-4">
              <Text className="text-lg font-bold text-white">Kies een Sticker</Text>
              <Pressable onPress={() => setShowStickerPicker(false)}>
                <Ionicons name="close" size={24} color="#9CA3AF" />
              </Pressable>
            </View>

            {/* Category Tabs */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} className="mb-4">
              {STICKER_CATEGORIES.map((cat, index) => (
                <Pressable
                  key={cat.name}
                  onPress={() => setStickerCategory(index)}
                  className={`px-4 py-2 rounded-full mr-2 ${
                    stickerCategory === index ? "bg-primary-600" : "bg-gray-700"
                  }`}
                >
                  <Text className="text-white font-medium">{cat.name}</Text>
                </Pressable>
              ))}
            </ScrollView>

            {/* Sticker Grid */}
            <ScrollView showsVerticalScrollIndicator={false}>
              <View className="flex-row flex-wrap justify-center">
                {STICKER_CATEGORIES[stickerCategory].items.map((emoji) => (
                  <Pressable
                    key={emoji}
                    onPress={() => handleAddSticker(emoji)}
                    className="w-16 h-16 items-center justify-center m-1 bg-gray-700 rounded-xl active:bg-gray-600"
                  >
                    <Text className="text-4xl">{emoji}</Text>
                  </Pressable>
                ))}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Text Input Modal */}
      <Modal visible={showTextModal} animationType="fade" transparent>
        <View className="flex-1 bg-black/50 items-center justify-center px-4">
          <View className="bg-gray-800 rounded-2xl p-6 w-full max-w-sm">
            <Text className="text-lg font-bold text-white mb-4">Voeg Tekst Toe</Text>
            
            <TextInput
              className="bg-gray-700 rounded-xl px-4 py-3 text-white mb-4"
              placeholder="Typ hier..."
              placeholderTextColor="#9CA3AF"
              value={newTextValue}
              onChangeText={setNewTextValue}
              autoFocus
              maxLength={20}
            />

            {/* Text Color */}
            <Text className="text-gray-400 text-xs mb-2">Tekstkleur</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} className="mb-4">
              {COLORS.slice(0, 12).map((c) => (
                <Pressable
                  key={c}
                  onPress={() => setSelectedColor(c)}
                  className="mr-2"
                >
                  <View
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 16,
                      backgroundColor: c,
                      borderWidth: selectedColor === c ? 3 : 1,
                      borderColor: selectedColor === c ? "#7C3AED" : "#4B5563",
                    }}
                  />
                </Pressable>
              ))}
            </ScrollView>

            <View className="flex-row">
              <Pressable
                onPress={() => {
                  setShowTextModal(false);
                  setNewTextValue("");
                }}
                className="flex-1 py-3 mr-2 items-center rounded-xl bg-gray-700"
              >
                <Text className="text-white font-medium">Annuleer</Text>
              </Pressable>
              <Pressable
                onPress={handleAddText}
                disabled={!newTextValue.trim()}
                className={`flex-1 py-3 ml-2 items-center rounded-xl ${
                  newTextValue.trim() ? "bg-primary-600" : "bg-gray-600"
                }`}
              >
                <Text className="text-white font-medium">Toevoegen</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit Text Modal */}
      <Modal visible={showEditTextModal} animationType="fade" transparent>
        <View className="flex-1 bg-black/50 items-center justify-center px-4">
          <View className="bg-gray-800 rounded-2xl p-6 w-full max-w-sm">
            <Text className="text-lg font-bold text-white mb-4">Tekst Bewerken</Text>
            
            <TextInput
              className="bg-gray-700 rounded-xl px-4 py-3 text-white mb-4"
              placeholder="Typ hier..."
              placeholderTextColor="#9CA3AF"
              value={editTextValue}
              onChangeText={setEditTextValue}
              autoFocus
              maxLength={20}
            />

            {/* Text Color */}
            <Text className="text-gray-400 text-xs mb-2">Tekstkleur</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} className="mb-4">
              {COLORS.slice(0, 12).map((c) => (
                <Pressable
                  key={c}
                  onPress={() => setSelectedColor(c)}
                  className="mr-2"
                >
                  <View
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 16,
                      backgroundColor: c,
                      borderWidth: selectedColor === c ? 3 : 1,
                      borderColor: selectedColor === c ? "#7C3AED" : "#4B5563",
                    }}
                  />
                </Pressable>
              ))}
            </ScrollView>

            <View className="flex-row">
              <Pressable
                onPress={() => {
                  setShowEditTextModal(false);
                  setEditingTextId(null);
                  setEditTextValue("");
                }}
                className="flex-1 py-3 mr-2 items-center rounded-xl bg-gray-700"
              >
                <Text className="text-white font-medium">Annuleer</Text>
              </Pressable>
              <Pressable
                onPress={handleSaveEditText}
                disabled={!editTextValue.trim()}
                className={`flex-1 py-3 ml-2 items-center rounded-xl ${
                  editTextValue.trim() ? "bg-primary-600" : "bg-gray-600"
                }`}
              >
                <Text className="text-white font-medium">Opslaan</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* Color Picker Modal */}
      <Modal visible={showColorPicker} animationType="fade" transparent>
        <View className="flex-1 bg-black/50 items-center justify-center px-4">
          <ScrollView 
            contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', paddingVertical: 20 }}
            showsVerticalScrollIndicator={false}
          >
            <View className="bg-gray-800 rounded-2xl p-6 w-full max-w-sm self-center">
              <Text className="text-lg font-bold text-white mb-2">Achtergrondkleur</Text>
              <Text className="text-gray-400 text-xs mb-3">Effen kleuren</Text>
              
              <View className="flex-row flex-wrap justify-center mb-4">
                {COLORS.map((c) => (
                  <Pressable
                    key={c}
                    onPress={() => {
                      setBackgroundColor(c);
                      setBackgroundGradient(null);
                      setBackgroundImage(null);
                      setShowColorPicker(false);
                    }}
                    className="m-1"
                  >
                    <View
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        backgroundColor: c,
                        borderWidth: backgroundColor === c && !backgroundGradient ? 3 : 0,
                        borderColor: "#7C3AED",
                      }}
                    />
                  </Pressable>
                ))}
              </View>

              {/* Gradients */}
              <Text className="text-gray-400 text-xs mb-3">Verlopen 🌈</Text>
              <View className="flex-row flex-wrap justify-center mb-4">
                {GRADIENTS.map((g) => (
                  <Pressable
                    key={g.id}
                    onPress={() => {
                      setBackgroundGradient(g.colors);
                      setBackgroundImage(null);
                      setShowColorPicker(false);
                    }}
                    className="m-1"
                  >
                    <LinearGradient
                      colors={g.colors as [string, string, ...string[]]}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                      style={{
                        width: 60,
                        height: 40,
                        borderRadius: 8,
                        borderWidth: backgroundGradient === g.colors ? 3 : 0,
                        borderColor: "#7C3AED",
                      }}
                    />
                    <Text className="text-gray-400 text-xs text-center mt-1">{g.name}</Text>
                  </Pressable>
                ))}
              </View>

              <Pressable
                onPress={() => setShowColorPicker(false)}
                className="py-3 items-center rounded-xl bg-gray-700"
              >
                <Text className="text-white font-medium">Sluiten</Text>
              </Pressable>
            </View>
          </ScrollView>
        </View>
      </Modal>

      {/* Shape Picker Modal */}
      <Modal visible={showShapePicker} animationType="slide" transparent>
        <View className="flex-1 bg-black/50 justify-end">
          <View className="bg-gray-800 rounded-t-3xl pt-4 pb-8 px-4">
            <View className="flex-row items-center justify-between mb-4">
              <Text className="text-lg font-bold text-white">Kies een Vorm</Text>
              <Pressable onPress={() => setShowShapePicker(false)}>
                <Ionicons name="close" size={24} color="#9CA3AF" />
              </Pressable>
            </View>

            {/* Color selection for shape */}
            <Text className="text-gray-400 text-xs mb-2">Kleur voor vorm</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} className="mb-4">
              {COLORS.map((c) => (
                <Pressable
                  key={c}
                  onPress={() => setSelectedColor(c)}
                  className="mr-2"
                >
                  <View
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 18,
                      backgroundColor: c,
                      borderWidth: selectedColor === c ? 3 : 1,
                      borderColor: selectedColor === c ? "#7C3AED" : "#4B5563",
                    }}
                  />
                </Pressable>
              ))}
            </ScrollView>

            {/* Shape Grid */}
            <View className="flex-row justify-center flex-wrap">
              {SHAPES.map((shape) => (
                <Pressable
                  key={shape.id}
                  onPress={() => handleAddShape(shape.id)}
                  className="w-20 h-20 items-center justify-center m-2 bg-gray-700 rounded-xl active:bg-gray-600"
                >
                  <Text style={{ fontSize: 36, color: selectedColor }}>{shape.icon}</Text>
                  <Text className="text-gray-400 text-xs mt-1">{shape.label}</Text>
                </Pressable>
              ))}
            </View>
          </View>
        </View>
      </Modal>

      {/* Sticker Pack Picker Modal */}
      <Modal visible={showStickerPackPicker} animationType="slide" transparent>
        <View className="flex-1 bg-black/50 justify-end">
          <View className="bg-gray-800 rounded-t-3xl pt-4 pb-8 px-4" style={{ maxHeight: "80%" }}>
            <View className="flex-row items-center justify-between mb-4">
              <View className="flex-row items-center">
                {selectedStickerPack && (
                  <Pressable 
                    onPress={() => setSelectedStickerPack(null)}
                    className="mr-3"
                  >
                    <Ionicons name="arrow-back" size={24} color="#9CA3AF" />
                  </Pressable>
                )}
                <Text className="text-lg font-bold text-white">
                  {selectedStickerPack ? selectedStickerPack.name : "Sticker Pakketten"}
                </Text>
              </View>
              <Pressable onPress={() => {
                setShowStickerPackPicker(false);
                setSelectedStickerPack(null);
              }}>
                <Ionicons name="close" size={24} color="#9CA3AF" />
              </Pressable>
            </View>

            {selectedStickerPack ? (
              /* Show stickers in selected pack */
              <ScrollView showsVerticalScrollIndicator={false}>
                <View className="flex-row flex-wrap justify-center">
                  {selectedStickerPack.stickers.map((sticker) => (
                    <Pressable
                      key={sticker.slug}
                      onPress={() => handleAddPackSticker(sticker)}
                      className="w-20 h-20 items-center justify-center m-1 bg-gray-700 rounded-xl active:bg-gray-600"
                    >
                      <Image
                        source={sticker.source}
                        style={{ width: 48, height: 48 }}
                        resizeMode="contain"
                      />
                    </Pressable>
                  ))}
                </View>
              </ScrollView>
            ) : (
              /* Show pack list */
              <ScrollView showsVerticalScrollIndicator={false}>
                {STICKER_PACKS.map((pack) => (
                  <Pressable
                    key={pack.slug}
                    onPress={() => setSelectedStickerPack(pack)}
                    className="flex-row items-center bg-gray-700 rounded-xl p-4 mb-3 active:bg-gray-600"
                  >
                    <View 
                      className="w-12 h-12 rounded-full items-center justify-center mr-4"
                      style={{ backgroundColor: pack.color }}
                    >
                      <Ionicons name={pack.icon as any} size={24} color="#FFFFFF" />
                    </View>
                    <View className="flex-1">
                      <Text className="text-white font-semibold text-base">{pack.name}</Text>
                      <Text className="text-gray-400 text-sm">{pack.description}</Text>
                      <Text className="text-gray-500 text-xs mt-1">
                        {pack.stickers.length} stickers
                      </Text>
                    </View>
                    <Ionicons name="chevron-forward" size={20} color="#6B7280" />
                  </Pressable>
                ))}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Drawing Modal */}
      <DrawingModal
        visible={showDrawingModal}
        onClose={() => setShowDrawingModal(false)}
        onSave={handleSaveDrawing}
        canvasSize={CANVAS_SIZE}
      />
      </SafeAreaView>
    </GestureHandlerRootView>
  );
}
